/**
 * Browser entry point.
 *
 * BrowserRouter (rather than HashRouter) means real URLs — /privacy, not
 * /#/privacy — which is what lets them be indexed, shared and used as the
 * privacy-policy URL an app store requires. It depends on the server returning
 * index.html for unknown paths, which apps/worker/src/app.ts does.
 */
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { registerServiceWorker } from './app/lib/register-sw';
import './styles/base.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('No #root element in index.html — the app has nowhere to mount.');
}

const tree = (
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>
);

/*
 * A public page arrives with its content already in it (rendered at build
 * time — src/client/entry-prerender.tsx), and React takes it over where it
 * stands rather than drawing it again: no flash, and the page reads before
 * its script arrives. Everything else — the app, a development server — is
 * drawn from nothing, as before.
 */
if (container.firstElementChild && !window.location.pathname.startsWith('/app')) {
  hydrateRoot(container, tree);
} else {
  createRoot(container).render(tree);
}

/*
 * The service worker is what makes the app installable and offline-capable.
 *
 * Registered after render rather than before, so its install — which fetches
 * and caches the shell — competes with nothing for bandwidth during the first
 * paint. It is scoped to /app; the marketing site is not an app shell.
 */
registerServiceWorker();
